// M5.8: player-facing team reward detail page. Shows the
// hero (name, image, description), the progress bar, the
// contributors list, and the contribute form.
//
// The page is reached from the TeamRewardCard on the player
// dashboard (or from the "View all" link). The route is
// /rewards/:id — a top-level player route, not under /player,
// because the contribute action is a focus state and the
// user can come back to it from anywhere in the app.

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  getMyTeamReward,
  contribute,
  getRewardContributors,
  getCoinBalance,
  ApiError,
} from './api';
import type { TeamReward, RewardContributor } from './types';

export function TeamRewardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const teamId = search.get('team_id');
  const qc = useQueryClient();

  const reward = useQuery({
    queryKey: ['me-team-reward', id],
    queryFn: () => getMyTeamReward(id!),
    enabled: !!id,
  });
  const contributors = useQuery({
    queryKey: ['me-team-reward-contributors', id],
    queryFn: () => getRewardContributors(id!),
    enabled: !!id,
  });
  const balance = useQuery({
    queryKey: ['me-coins'],
    queryFn: getCoinBalance,
  });

  // M5.8: local toast for the contribution success. We
  // don't share state with the player dashboard's
  // useCelebrations hook (which is keyed on badges/level/
  // streak diffs) — coin contributions are user-initiated
  // and the page knows when one succeeds. A simple
  // local toast is enough; the celebration overlay is
  // for system-detected wins.
  const [toast, setToast] = useState<{ id: number; title: string; detail: string } | null>(null);

  const contributeMut = useMutation({
    mutationFn: (amount: number) => contribute(id!, amount),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['me-team-reward', id] });
      qc.invalidateQueries({ queryKey: ['me-team-reward-contributors', id] });
      qc.invalidateQueries({ queryKey: ['me-team-rewards'] });
      qc.invalidateQueries({ queryKey: ['me-coins'] });
      qc.invalidateQueries({ queryKey: ['me-coin-transactions'] });
      setToast({
        id: Date.now(),
        title: '🪙 Contribution accepted',
        detail: result.remaining === 0
          ? 'You filled the pool! The team can claim this reward now.'
          : `Pool is at ${result.pool_total} coins. ${result.remaining} more to fully fund.`,
      });
    },
  });
  // Auto-dismiss the toast after 3.5s.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  if (!id) return null;
  if (reward.isPending) {
    return <p className="text-sm text-ink-muted">Loading…</p>;
  }
  if (reward.isError) {
    return (
      <div className="card">
        <p className="text-sm text-err">
          {(reward.error as ApiError).message || 'Could not load this reward.'}
        </p>
        <Link to="/player" className="mt-2 inline-block text-sm underline">← Back to dashboard</Link>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <Link to={teamId ? `/player` : '/player'} className="text-sm text-ink-muted underline">
          ← Back to dashboard
        </Link>
      </div>
      <RewardHero reward={reward.data!} />
      <ContributePanel
        reward={reward.data!}
        balance={balance.data ?? 0}
        error={contributeMut.error as ApiError | null}
        isPending={contributeMut.isPending}
        onSubmit={(amount) => contributeMut.mutate(amount)}
        onSuccess={() => {
          // Navigate back after a moment to let the user see
          // the celebration overlay.
          setTimeout(() => navigate('/player'), 2000);
        }}
      />
      <ContributorsList
        contributors={contributors.data ?? []}
        isLoading={contributors.isPending}
        myUserId={null /* not in scope; the UI shows all contributors equally */}
      />
      {toast ? <ContributionToast title={toast.title} detail={toast.detail} /> : null}
    </div>
  );
}

function ContributionToast({ title, detail }: { title: string; detail: string }) {
  return (
    <div
      className="pointer-events-none fixed right-4 top-20 z-50 w-72 rounded-lg border border-accent/40 bg-bg-panel p-4 shadow-2xl"
      role="status"
      aria-live="polite"
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-xs text-ink-muted">{detail}</p>
    </div>
  );
}

function RewardHero({ reward }: { reward: TeamReward }) {
  return (
    <div className="card">
      {reward.image_url ? (
        <img
          src={reward.image_url}
          alt=""
          className="mb-3 h-40 w-full rounded-md object-cover"
        />
      ) : null}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{reward.name}</h1>
          {reward.description ? (
            <p className="mt-2 text-sm text-ink-muted whitespace-pre-line">
              {reward.description}
            </p>
          ) : null}
        </div>
        <StatusBadge status={reward.status} />
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-muted">Pool progress</span>
          <span className="font-mono">
            <span aria-hidden>🪙</span> {reward.contributed} / {reward.coin_cost}
          </span>
        </div>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-bg-panel">
          <div
            className="h-full bg-gradient-to-r from-accent to-accent-success"
            style={{ width: `${reward.progress_pct}%` }}
            role="progressbar"
            aria-valuenow={reward.contributed}
            aria-valuemin={0}
            aria-valuemax={reward.coin_cost}
          />
        </div>
        <p className="mt-1 text-xs text-ink-dim">
          {reward.remaining} more coin{reward.remaining === 1 ? '' : 's'} needed
          {reward.status === 'claimed' ? ' · this reward has been claimed' : null}
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TeamReward['status'] }) {
  if (status === 'open') {
    return <span className="rounded bg-accent/20 px-2 py-0.5 text-xs text-accent">Open</span>;
  }
  if (status === 'claimed') {
    return (
      <span className="rounded bg-accent-success/20 px-2 py-0.5 text-xs text-accent-success">
        Claimed
      </span>
    );
  }
  return (
    <span className="rounded bg-bg-elevated px-2 py-0.5 text-xs text-ink-muted">
      Archived
    </span>
  );
}

function ContributePanel({
  reward,
  balance,
  error,
  isPending,
  onSubmit,
  onSuccess,
}: {
  reward: TeamReward;
  balance: number;
  error: ApiError | null;
  isPending: boolean;
  onSubmit: (amount: number) => void;
  onSuccess: () => void;
}) {
  // Default to min(remaining, balance). Floor at 0 for the
  // "fully funded" / "claimed" cases.
  const defaultAmount = Math.max(0, Math.min(reward.remaining, balance));
  const [amount, setAmount] = useState(defaultAmount);
  // Reset when the default changes (e.g. another player
  // contributed in another tab and the remaining went down).
  useEffect(() => {
    setAmount(defaultAmount);
  }, [defaultAmount]);
  const canContribute = reward.status === 'open' && reward.remaining > 0 && balance > 0;
  if (!canContribute) {
    return (
      <div className="card">
        <p className="text-sm text-ink-muted">
          {reward.status === 'claimed'
            ? 'This reward has been claimed. Thanks for your contributions!'
            : balance === 0
            ? 'You have no coins to contribute. Earn coins by shipping work, giving kudos, or completing quests.'
            : 'This reward is fully funded. Ready to claim soon!'}
        </p>
      </div>
    );
  }
  return (
    <form
      className="card space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(amount);
      }}
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Contribute
      </p>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="label" htmlFor="amt">Amount (in coins)</label>
          <input
            id="amt"
            className="input"
            type="number"
            min={1}
            max={Math.min(reward.remaining, balance)}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            required
          />
        </div>
        <div className="text-right">
          <p className="text-xs text-ink-muted">Your balance</p>
          <p className="font-mono text-lg"><span aria-hidden>🪙</span> {balance}</p>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-ink-dim">
        <span>
          Max: {Math.min(reward.remaining, balance)} coins
          {amount > reward.remaining ? ' (over-cap would be rejected)' : null}
        </span>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setAmount(Math.min(reward.remaining, balance))}
        >
          Use max
        </button>
      </div>
      {error ? (
        <p className="text-sm text-err" role="alert">{error.message}</p>
      ) : null}
      <div className="flex items-center justify-end gap-2">
        <button
          type="submit"
          className="btn-primary"
          disabled={isPending || amount < 1 || amount > Math.min(reward.remaining, balance)}
        >
          {isPending ? 'Contributing…' : `Contribute ${amount} coins`}
        </button>
      </div>
    </form>
  );
}

function ContributorsList({
  contributors,
  isLoading,
  myUserId: _myUserId,
}: {
  contributors: RewardContributor[];
  isLoading: boolean;
  myUserId: string | null;
}) {
  return (
    <div className="card">
      <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Contributors ({contributors.length})
      </p>
      {isLoading ? (
        <p className="mt-2 text-sm text-ink-muted">Loading…</p>
      ) : contributors.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">
          No one has contributed yet. Be the first!
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-white/5">
          {contributors.map((c) => (
            <li key={c.user_id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <p className="font-medium">{c.display_name}</p>
                <p className="text-xs text-ink-dim">@{c.handle}</p>
              </div>
              <div className="text-right">
                <p className="font-mono"><span aria-hidden>🪙</span> {c.total}</p>
                <p className="text-xs text-ink-dim">since {new Date(c.first_at).toLocaleDateString()}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
